import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Terminal,
  Mail,
  CheckCircle,
  ArrowRight,
  Shield,
  Zap,
  Package,
  Lock,
  ExternalLink,
  Sun,
  Moon,
} from 'lucide-react'
import { useTheme } from '../context/ThemeContext'

export default function ComingSoonPage() {
  const { theme, toggleTheme } = useTheme()
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showInterests, setShowInterests] = useState(false)
  const [toolInterests, setToolInterests] = useState('')
  const [interestsSubmitted, setInterestsSubmitted] = useState(false)
  const [interestsLoading, setInterestsLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''
      const response = await fetch(`${API_BASE_URL}/v1/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })

      if (!response.ok) {
        throw new Error('Failed to join waitlist')
      }

      setSubmitted(true)
      setShowInterests(true)
    } catch {
      setSubmitted(true)
      setShowInterests(true)
    } finally {
      setLoading(false)
    }
  }

  const handleInterestsSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!toolInterests.trim()) {
      setInterestsSubmitted(true)
      return
    }

    setInterestsLoading(true)
    try {
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''
      await fetch(`${API_BASE_URL}/v1/waitlist/interests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, tool_interests: toolInterests }),
      })
    } catch {
      // Silent fail - we already have their email
    } finally {
      setInterestsLoading(false)
      setInterestsSubmitted(true)
    }
  }

  const skipInterests = () => {
    setInterestsSubmitted(true)
  }

  return (
    <div className={`min-h-screen overflow-hidden transition-colors duration-300 ${
      theme === 'dark' ? 'bg-black text-white' : 'bg-white text-gray-900'
    }`}>
      {/* Animated background grid */}
      <div className="fixed inset-0 opacity-20">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: theme === 'dark'
              ? `linear-gradient(rgba(99, 102, 241, 0.1) 1px, transparent 1px),
                 linear-gradient(90deg, rgba(99, 102, 241, 0.1) 1px, transparent 1px)`
              : `linear-gradient(rgba(99, 102, 241, 0.15) 1px, transparent 1px),
                 linear-gradient(90deg, rgba(99, 102, 241, 0.15) 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      {/* Gradient orbs */}
      <div className={`fixed top-0 left-1/4 w-96 h-96 rounded-full blur-[128px] ${
        theme === 'dark' ? 'bg-indigo-600/30' : 'bg-indigo-400/20'
      }`} />
      <div className={`fixed bottom-0 right-1/4 w-96 h-96 rounded-full blur-[128px] ${
        theme === 'dark' ? 'bg-purple-600/20' : 'bg-purple-400/15'
      }`} />

      {/* Header */}
      <header className="relative z-10 py-6 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              theme === 'dark' ? 'bg-white' : 'bg-gray-900'
            }`}>
              <Terminal className={`w-6 h-6 ${theme === 'dark' ? 'text-black' : 'text-white'}`} />
            </div>
            <span className="text-xl font-semibold tracking-tight">xarlie</span>
          </div>
          <button
            onClick={toggleTheme}
            className={`p-2 rounded-lg transition-colors ${
              theme === 'dark'
                ? 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
            }`}
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
        </div>
      </header>

      {/* Hero */}
      <main className="relative z-10">
        <section className="min-h-[80vh] flex items-center px-6">
          <div className="max-w-7xl mx-auto w-full">
            <div className="max-w-3xl">
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm mb-8 ${
                theme === 'dark'
                  ? 'bg-zinc-900 border border-zinc-800 text-zinc-400'
                  : 'bg-gray-100 border border-gray-200 text-gray-600'
              }`}>
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                Coming Soon
              </div>

              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.1] tracking-tight mb-6">
                Build agent skills.{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400">
                  Deploy anywhere.
                </span>
              </h1>

              <p className={`text-xl leading-relaxed mb-10 max-w-2xl ${
                theme === 'dark' ? 'text-zinc-400' : 'text-gray-600'
              }`}>
                Discover, publish, and securely integrate skills for AI agents.
                Every skill reviewed. Every connection secured.
              </p>

              {/* Email signup flow */}
              {!submitted ? (
                <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-lg">
                  <div className="relative flex-1">
                    <Mail className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${
                      theme === 'dark' ? 'text-zinc-500' : 'text-gray-400'
                    }`} />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      required
                      className={`w-full pl-12 pr-4 py-3.5 rounded-xl focus:outline-none focus:ring-1 focus:ring-indigo-500 transition-colors ${
                        theme === 'dark'
                          ? 'bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-500 focus:border-indigo-500'
                          : 'bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:border-indigo-500'
                      }`}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className={`px-6 py-3.5 font-medium rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2 ${
                      theme === 'dark'
                        ? 'bg-white text-black hover:bg-zinc-200'
                        : 'bg-gray-900 text-white hover:bg-gray-800'
                    }`}
                  >
                    {loading ? 'Joining...' : 'Get Early Access'}
                    {!loading && <ArrowRight className="w-4 h-4" />}
                  </button>
                </form>
              ) : showInterests && !interestsSubmitted ? (
                <div className="max-w-lg space-y-4">
                  <div className={`inline-flex items-center gap-3 px-4 py-3 rounded-xl ${
                    theme === 'dark'
                      ? 'bg-emerald-500/10 border border-emerald-500/30'
                      : 'bg-emerald-50 border border-emerald-200'
                  }`}>
                    <CheckCircle className={theme === 'dark' ? 'w-5 h-5 text-emerald-400' : 'w-5 h-5 text-emerald-600'} />
                    <span className={theme === 'dark' ? 'text-emerald-300' : 'text-emerald-700'}>
                      You're on the list!
                    </span>
                  </div>
                  <p className={`text-sm ${theme === 'dark' ? 'text-zinc-400' : 'text-gray-600'}`}>
                    One more thing - what AI skill would you most like to see, use, or build?
                  </p>
                  <form onSubmit={handleInterestsSubmit} className="space-y-3">
                    <textarea
                      value={toolInterests}
                      onChange={(e) => setToolInterests(e.target.value)}
                      placeholder="e.g., Slack integration, GitHub skills, database connectors, custom API wrappers..."
                      rows={3}
                      className={`w-full px-4 py-3 rounded-xl focus:outline-none focus:ring-1 focus:ring-indigo-500 transition-colors resize-none ${
                        theme === 'dark'
                          ? 'bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-500 focus:border-indigo-500'
                          : 'bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:border-indigo-500'
                      }`}
                    />
                    <div className="flex gap-3">
                      <button
                        type="submit"
                        disabled={interestsLoading}
                        className={`px-5 py-2.5 font-medium rounded-xl transition-colors disabled:opacity-50 ${
                          theme === 'dark'
                            ? 'bg-white text-black hover:bg-zinc-200'
                            : 'bg-gray-900 text-white hover:bg-gray-800'
                        }`}
                      >
                        {interestsLoading ? 'Submitting...' : 'Submit'}
                      </button>
                      <button
                        type="button"
                        onClick={skipInterests}
                        className={`px-5 py-2.5 font-medium rounded-xl transition-colors ${
                          theme === 'dark'
                            ? 'text-zinc-400 hover:text-white'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        Skip
                      </button>
                    </div>
                  </form>
                </div>
              ) : (
                <div className={`inline-flex items-center gap-3 px-6 py-4 rounded-xl ${
                  theme === 'dark'
                    ? 'bg-emerald-500/10 border border-emerald-500/30'
                    : 'bg-emerald-50 border border-emerald-200'
                }`}>
                  <CheckCircle className={theme === 'dark' ? 'w-5 h-5 text-emerald-400' : 'w-5 h-5 text-emerald-600'} />
                  <span className={theme === 'dark' ? 'text-emerald-300' : 'text-emerald-700'}>
                    Thanks! We'll be in touch soon.
                  </span>
                </div>
              )}

            </div>
          </div>
        </section>

        {/* What is MCP */}
        <section className={`py-24 px-6 border-t ${theme === 'dark' ? 'border-zinc-900' : 'border-gray-200'}`}>
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl font-bold mb-6">What is MCP?</h2>
                <p className={`text-lg leading-relaxed mb-8 ${theme === 'dark' ? 'text-zinc-400' : 'text-gray-600'}`}>
                  Model Context Protocol (MCP) is an open standard for connecting
                  AI assistants to external skills. It lets AI systems safely
                  interact with APIs, databases, and services.
                </p>
                <div className="space-y-4">
                  {[
                    'Open protocol backed by Anthropic',
                    'Works with Claude, ChatGPT, and other LLMs',
                    'Secure sandboxed execution',
                  ].map((item) => (
                    <div key={item} className={`flex items-center gap-3 ${theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'}`}>
                      <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                      {item}
                    </div>
                  ))}
                </div>
              </div>
              <div className={`rounded-2xl border p-6 font-mono text-sm ${
                theme === 'dark' ? 'bg-zinc-900 border-zinc-800' : 'bg-gray-900 border-gray-800'
              }`}>
                <div className="flex items-center gap-2 mb-4 text-zinc-500">
                  <div className="w-3 h-3 rounded-full bg-zinc-700" />
                  <div className="w-3 h-3 rounded-full bg-zinc-700" />
                  <div className="w-3 h-3 rounded-full bg-zinc-700" />
                </div>
                <div className="space-y-2">
                  <div className="text-zinc-500"># Install a skill from xarlie</div>
                  <div className="text-emerald-400">$ xarlie install @acme/weather</div>
                  <div className="text-zinc-400 mt-4">Installing @acme/weather@1.2.0...</div>
                  <div className="text-zinc-400">Verifying signatures...</div>
                  <div className="text-emerald-400">Done in 1.2s</div>
                  <div className="text-zinc-500 mt-4"># Use it immediately</div>
                  <div className="text-indigo-400">
                    {'>'} What's the weather in Tokyo?
                  </div>
                  <div className="text-zinc-300 mt-2">
                    The current weather in Tokyo is 18C, partly cloudy.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Xarlie */}
        <section className={`py-24 px-6 ${theme === 'dark' ? 'bg-zinc-950' : 'bg-gray-50'}`}>
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold mb-4 text-center">Why Xarlie?</h2>
            <p className={`text-center mb-16 max-w-2xl mx-auto ${theme === 'dark' ? 'text-zinc-500' : 'text-gray-600'}`}>
              Running untrusted code is dangerous. We're building the npm for AI skills.
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: Shield,
                  title: 'Security First',
                  description:
                    'Every skill undergoes security review. Sandboxed execution. No surprise behaviors.',
                },
                {
                  icon: Zap,
                  title: 'One Command',
                  description:
                    'Install any skill with xarlie install. Dependencies, updates, and versioning handled.',
                },
                {
                  icon: Lock,
                  title: 'Enterprise Ready',
                  description:
                    'SSO, audit logs, private registries, and compliance controls for teams.',
                },
              ].map((feature) => (
                <div
                  key={feature.title}
                  className={`p-6 rounded-2xl border transition-colors ${
                    theme === 'dark'
                      ? 'bg-zinc-900/50 border-zinc-800 hover:border-zinc-700'
                      : 'bg-white border-gray-200 hover:border-gray-300 shadow-sm'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 ${
                    theme === 'dark' ? 'bg-indigo-500/10' : 'bg-indigo-100'
                  }`}>
                    <feature.icon className={`w-6 h-6 ${theme === 'dark' ? 'text-indigo-400' : 'text-indigo-600'}`} />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                  <p className={`leading-relaxed ${theme === 'dark' ? 'text-zinc-400' : 'text-gray-600'}`}>{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className={`py-24 px-6 border-t ${theme === 'dark' ? 'border-zinc-900' : 'border-gray-200'}`}>
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold mb-16 text-center">How it works</h2>
            <div className="grid md:grid-cols-4 gap-8">
              {[
                { step: '01', title: 'Browse', description: 'Find skills by category, capability, or search' },
                { step: '02', title: 'Install', description: 'One command to add any skill to your stack' },
                { step: '03', title: 'Configure', description: 'Set permissions and connect accounts' },
                { step: '04', title: 'Use', description: 'Your AI assistant discovers skills automatically' },
              ].map((item) => (
                <div key={item.step} className="text-center">
                  <div className={`text-5xl font-bold mb-4 ${theme === 'dark' ? 'text-zinc-800' : 'text-gray-200'}`}>{item.step}</div>
                  <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                  <p className={`text-sm ${theme === 'dark' ? 'text-zinc-500' : 'text-gray-500'}`}>{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* For Publishers */}
        <section className={`py-24 px-6 ${
          theme === 'dark'
            ? 'bg-gradient-to-b from-zinc-950 to-black'
            : 'bg-gradient-to-b from-gray-50 to-white'
        }`}>
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm mb-6 ${
                  theme === 'dark'
                    ? 'bg-zinc-900 border border-zinc-800 text-zinc-400'
                    : 'bg-gray-100 border border-gray-200 text-gray-600'
                }`}>
                  <Package className="w-4 h-4" />
                  For Publishers
                </div>
                <h2 className="text-3xl font-bold mb-6">Share your skills with the world</h2>
                <p className={`text-lg leading-relaxed mb-8 ${theme === 'dark' ? 'text-zinc-400' : 'text-gray-600'}`}>
                  Publish your MCP skills to thousands of developers. Free for open source.
                  Monetization options for commercial skills.
                </p>
                <div className="space-y-4 mb-8">
                  {[
                    'Simple manifest-based publishing',
                    'Automatic version management',
                    'Usage analytics and feedback',
                    'Revenue sharing for paid skills',
                  ].map((item) => (
                    <div key={item} className={`flex items-center gap-3 ${theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'}`}>
                      <CheckCircle className={`w-5 h-5 flex-shrink-0 ${theme === 'dark' ? 'text-indigo-400' : 'text-indigo-600'}`} />
                      {item}
                    </div>
                  ))}
                </div>
                <Link
                  to="/publisher/signup"
                  className={`inline-flex items-center gap-2 transition-colors ${
                    theme === 'dark' ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-700'
                  }`}
                >
                  Apply to publish
                  <ExternalLink className="w-4 h-4" />
                </Link>
              </div>
              <div className={`rounded-2xl border p-6 font-mono text-sm ${
                theme === 'dark' ? 'bg-zinc-900 border-zinc-800' : 'bg-gray-900 border-gray-800'
              }`}>
                <div className="text-zinc-500 mb-2">xarlie.json</div>
                <pre className="text-zinc-300 overflow-x-auto">
{`{
  "name": "@acme/weather-skill",
  "version": "1.0.0",
  "description": "Weather data",
  "license": "MIT",
  "open_source": true,
  "capabilities": [
    "weather.current",
    "weather.forecast"
  ],
  "permissions": {
    "network": ["api.weather.com"]
  }
}`}
                </pre>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-32 px-6 text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-4xl font-bold mb-6">Be first in line</h2>
            <p className={`mb-10 ${theme === 'dark' ? 'text-zinc-400' : 'text-gray-600'}`}>
              We're launching soon. Get early access and help shape the future of AI skills.
            </p>
            {!submitted && (
              <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                  className={`flex-1 px-4 py-3.5 rounded-xl focus:outline-none focus:border-indigo-500 ${
                    theme === 'dark'
                      ? 'bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-500'
                      : 'bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400'
                  }`}
                />
                <button
                  type="submit"
                  disabled={loading}
                  className={`px-6 py-3.5 font-medium rounded-xl transition-colors ${
                    theme === 'dark'
                      ? 'bg-white text-black hover:bg-zinc-200'
                      : 'bg-gray-900 text-white hover:bg-gray-800'
                  }`}
                >
                  Join Waitlist
                </button>
              </form>
            )}
          </div>
        </section>

        {/* Footer */}
        <footer className={`py-8 px-6 border-t ${theme === 'dark' ? 'border-zinc-900' : 'border-gray-200'}`}>
          <div className={`max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-sm ${
            theme === 'dark' ? 'text-zinc-600' : 'text-gray-500'
          }`}>
            <div className="flex items-center gap-2">
              <Terminal className="w-4 h-4" />
              <span>xarlie</span>
            </div>
            <div>&copy; {new Date().getFullYear()} Xarlie</div>
          </div>
        </footer>
      </main>
    </div>
  )
}
